import {useMutation, UseMutationResult, useQueryClient, UseQueryResult} from '@tanstack/react-query';
import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {QUERY_KEYS} from '../../../utils/queryKeys';
import Button from '../../../incase-domain/components/button/Button';
import DropdownMenuCompact, {
    DropdownMenuCompactMenuPosition,
} from '../../../incase-domain/components/dropdownMenuCompact/DropdownMenuCompact';
import ConfirmModal from '../../../incase-domain/components/modals/confirmModal/ConfirmModal';
import Modal from '../../../incase-domain/components/modals/directory/Modal';
import {IConnection} from '../../../incase-domain/connection';
import {IPaginatedResult} from '../../../incase-domain/search';
import {removeConnection} from '../../../service/connection.service';
import {IUpdateError} from '../../../store/actions/types/error.types';
import DataView, {ViewMode} from '../../components/common/data/DataView';
import {createDataViewListStateProps, ListParams, SetListParamsOptions} from '../../../hooks/listParams';
import ConnectionsModalAddConnection from './ConnectionsModalAddConnection';

interface ConnectionsTableActiveProps {
    connections: UseQueryResult<IPaginatedResult<IConnection>>;
    listParams: ListParams;
    setListParams: (partial: Partial<ListParams>, options?: SetListParamsOptions) => void;
    onNewConnectionRequestAdded: () => void;
}

function ConnectionsTableActive(props: ConnectionsTableActiveProps): JSX.Element {
    const {connections, listParams, setListParams, onNewConnectionRequestAdded} = props;

    const dataViewListState = createDataViewListStateProps(listParams, setListParams);

    const {t} = useTranslation();
    const client = useQueryClient();

    const [selectedConnectionId, setSelectedConnectionId] = useState<string>('');
    const [modalOpened, setIsModalOpened] = useState<boolean>(false);
    const [openModalNewConnection, setOpenModalNewConnection] = useState<boolean>(false);

    const removeSelectedConnection: UseMutationResult<IConnection, IUpdateError> = useMutation(
        () => removeConnection(selectedConnectionId),
        {
            onSuccess: () => {
                client.invalidateQueries({queryKey: QUERY_KEYS.connection.all});
                client.invalidateQueries({queryKey: QUERY_KEYS.auth.all});

                setIsModalOpened(false);
                setSelectedConnectionId('');
            },
        }
    );

    function onNewConnection(): void {
        setOpenModalNewConnection(true);
    }

    const renderUserImage = (): JSX.Element => {
        return <Button leading="solar:user-linear" variant="soft" size="sm" rounded square disablePointerEvents />;
    };

    const renderUsernameEmail = (connection: IConnection): JSX.Element => {
        return (
            <>
                <span>
                    <strong>{connection.username}</strong>
                </span>
                <span>
                    <p>{connection.email}</p>
                </span>
            </>
        );
    };

    const renderRole = (connection: IConnection): JSX.Element => {
        return connection.roles[0] ? (
            <Button variant="soft" size="sm" disablePointerEvents>
                {connection.roles[0]}
            </Button>
        ) : (
            <></>
        );
    };

    const renderActions = (connection: IConnection): JSX.Element => {
        return (
            <DropdownMenuCompact
                menuPosition={DropdownMenuCompactMenuPosition.LEFT}
                items={[
                    [
                        {
                            label: t('common.delete'),
                            onClick: () => {
                                setSelectedConnectionId(connection._id);
                                setIsModalOpened(true);
                            },
                            icon: 'solar:trash-bin-minimalistic-linear',
                            color: 'error',
                        },
                    ],
                ]}
            >
                <Button variant="ghost" size="sm" square leading="ph:dots-three-vertical-bold" />
            </DropdownMenuCompact>
        );
    };

    return (
        <>
            <DataView
                views={[
                    {
                        columns: [
                            {
                                body: () => renderUserImage(),
                                width: '3rem',
                            },
                            {
                                label: t('connections.user'),
                                body: (row) => renderUsernameEmail(row),
                            },
                            {
                                label: t('connections.role'),
                                body: (row) => renderRole(row),
                                width: '10rem',
                            },
                            {
                                body: (row) => renderActions(row),
                                width: '4rem',
                            },
                        ],
                        view: ViewMode.TABLE,
                        hideHeader: true,
                    },
                ]}
                rows={connections?.data?.content || []}
                rowIdentifier={(row) => row._id}
                loading={connections.isLoading || connections.isFetching}
                paginationData={{
                    pageSize: listParams.size,
                    currentPage: listParams.page,
                    itemsCount: connections?.data?.numberOfElements || 0,
                }}
                {...dataViewListState}
                headerActionPrimary={{
                    icon: 'ph:plus-bold',
                    onClick: onNewConnection,
                    title: t('genericTable.new'),
                }}
                enableSearch
            />
            <ConfirmModal
                isOpen={modalOpened}
                message={`${t('connections.deleteModal')}`}
                color="error"
                showCancel
                showConfirm
                cancelText={`${t('common.cancel')}`}
                confirmText={`${t('common.confirm')}`}
                onConfirm={() => removeSelectedConnection.mutate(selectedConnectionId)}
                onCancel={() => {
                    setIsModalOpened(false);
                    setSelectedConnectionId('');
                }}
            />
            <Modal
                size="lg"
                isOpen={openModalNewConnection}
                icon="solar:users-group-rounded-bold"
                header={`${t('connections.title')}`}
                description={t('connections.addNewConnection')}
                onCancel={() => {
                    setOpenModalNewConnection(false);
                    setSelectedConnectionId('');
                }}
            >
                <ConnectionsModalAddConnection onNewConnectionRequestAdded={onNewConnectionRequestAdded} />
            </Modal>
        </>
    );
}

export default ConnectionsTableActive;
